/**
 * Shared Gerstner ocean — the single most important shared module.
 *
 * The SAME closed-form height function is evaluated:
 *   - on the CLIENT, inside the vertex shader (generated GLSL below), to
 *     displace the sea mesh, and
 *   - on the SERVER (and client prediction), on the CPU, to sample water
 *     height at each ship's hull control points for deterministic buoyancy
 *     and cannon-muzzle world height.
 *
 * No FFT, no GPU readback, deterministic across every device. If you change a
 * wave constant here, BOTH the shader and the server buoyancy change together
 * because the GLSL is generated from these very numbers.
 *
 * Plane convention: p = (x, y) is the 2D sim plane (see protocol/config.ts).
 * In the client shader, pass p = (worldX, worldZ); the returned height is
 * world-Y (up).
 */

const G = 9.81;

interface WaveDef {
  /** direction angle of travel in the (x,y) plane, radians */
  angle: number;
  /** wavelength (meters) */
  length: number;
  /** amplitude (meters) */
  amp: number;
  /** Gerstner steepness 0..1 (horizontal pinch of the crest) */
  steep: number;
  /**
   * How much of the STORM swell scale this component takes, 0..1 (see `amp` in the samplers below).
   *
   * The sea does not simply get "×2 bigger" in a gale — the long SWELL rears up while the short
   * wind-chop is already near saturation and barely grows. The storm EXCESS of the scalar `amp`
   * therefore fans out per component by this weight (see `waveAmpAt`), which is why a storm reads as a
   * GIANT ROLLING VAGA with texture on it instead of a uniformly zoomed calm sea.
   *
   * The identity `amp = 1 ⇒ weight = 1 for every component` is what makes the approved calm sea
   * bit-for-bit untouched by the storm machinery (asserted in ocean.test.ts).
   */
  swell: number;
}

/**
 * Authoring table — tune the sea here.
 *
 * TWO BANDS, and the split is load-bearing:
 *
 *   SWELL (42 / 26 / 15 / 9 m) — the four components shipped in water-v1 and signed off by the owner.
 *   Not one digit of them has changed. They carry the silhouette: the long rolling shape the ship rides.
 *
 *   CHOP (6.4 → 1.42 m) — NEW. Five short, low components (4 cm down to 8 mm) continuing the cascade
 *   below the swell, i.e. a saturated wind-sea tail rather than four arbitrary sines. Their directions
 *   are SEMI-ALIGNED to the dominant swell axis (0.30 rad) but at deliberately IRREGULAR offsets
 *   (−0.38, +0.61, +1.05, −0.83, −1.32 rad), and the wavelengths deliberately avoid a clean geometric
 *   ratio — both because regular spacing makes the components beat against each other in a repeating
 *   pattern, which under a specular highlight reads as corduroy rather than as water.
 *
 * BUDGET THE SLOPE, NOT THE HEIGHT. The first cut of this band used ~2.4× these amplitudes. They still
 * looked tiny (7 cm on a 1.3 m sea) — but slope is A·k, and at 1.5 m wavelengths that handful of
 * centimetres carried as much total slope as the ENTIRE approved swell. Since slope is what specular
 * lighting integrates, the sea turned into a lit corduroy in every sun path. These amplitudes hold the
 * chop's slope to roughly half the swell's, which is what reads as texture on the water instead of a
 * second, finer sea laid over the first.
 *
 * Together they add 11 cm to a 1.32 m sea (+8%): the calm silhouette is UNCHANGED, what arrives is
 * detail crawling over it. Costs are measured and documented in `buildOceanNormalGLSL` (the per-pixel
 * consumer is the only one where five extra sin/cos are worth gating).
 */
const WAVE_DEFS: readonly WaveDef[] = [
  // ---- the approved SWELL (water-v1, unchanged — this is the sea the owner signed off on) ----
  { angle: 0.30, length: 42, amp: 0.6, steep: 0.55, swell: 1.0 },
  { angle: 1.05, length: 26, amp: 0.38, steep: 0.55, swell: 1.0 },
  { angle: 2.30, length: 15, amp: 0.22, steep: 0.5, swell: 0.85 },
  { angle: -0.85, length: 9, amp: 0.12, steep: 0.45, swell: 0.65 },
  // ---- the wind-sea CHOP band (new): centimetre-to-decimetre detail, near saturation already ----
  { angle: -0.08, length: 6.4, amp: 0.042, steep: 0.4, swell: 0.45 },
  { angle: 0.91, length: 4.1, amp: 0.028, steep: 0.38, swell: 0.4 },
  { angle: 1.35, length: 3.15, amp: 0.019, steep: 0.34, swell: 0.34 },
  { angle: -0.53, length: 2.05, amp: 0.012, steep: 0.3, swell: 0.28 },
  { angle: -1.02, length: 1.42, amp: 0.008, steep: 0.26, swell: 0.22 },
];

/**
 * Wavelength (m) at or above which a component counts as SWELL. Everything below is CHOP and may be
 * faded out by distance in the per-pixel normal (never in the vertex displacement — see
 * `buildOceanNormalGLSL`). Sits between the 9 m swell tail and the 6.2 m chop head.
 */
const CHOP_MAX_LEN = 7.5;

/** Precomputed per-wave coefficients (dir, wavenumber k, angular speed ω). */
export interface WaveCoef {
  dirX: number; dirY: number; k: number; omega: number; amp: number; steep: number;
  /** wavelength in metres (the authoring number, kept for the hull-response filter) */
  length: number;
  /** storm-swell participation, 0..1 — see `WaveDef.swell` */
  swell: number;
  /** true for the short wind-chop band (may be distance-faded in the per-pixel normal) */
  chop: boolean;
}

export const OCEAN_WAVES: readonly WaveCoef[] = WAVE_DEFS.map((w) => {
  const k = (Math.PI * 2) / w.length;
  return {
    dirX: Math.cos(w.angle),
    dirY: Math.sin(w.angle),
    k,
    omega: Math.sqrt(G * k), // deep-water dispersion => natural relative speeds
    amp: w.amp,
    steep: w.steep,
    length: w.length,
    swell: w.swell,
    chop: w.length < CHOP_MAX_LEN,
  };
});

/** How many components are the long SWELL band (the rest are chop). The GLSL emits swell first. */
export const OCEAN_SWELL_COUNT = OCEAN_WAVES.filter((w) => !w.chop).length;

/* ------------------------------------------------------------------------------------------------ *
 *  LIVE SEA STATE — the one piece of the ocean that is not a constant.
 * ------------------------------------------------------------------------------------------------ */

/**
 * The wind frame + chop gain the SHORT band is currently riding.
 *
 * WHY THIS IS MODULE STATE AND NOT A PARAMETER. The wave field has ~40 call sites across the renderer
 * (every splash, wake point, bob, camera clamp and FX anchor) and they must all agree with the shader to
 * the millimetre. Threading two more arguments through every one of them is forty chances to forget one,
 * and a forgotten one is a wake floating above the sea. There is exactly ONE sea, so there is exactly one
 * place its live state lives, and the renderer writes it once per frame next to the uniforms it mirrors
 * (`Renderer.updateSeaState`). That is the same contract `uTime` already has.
 *
 * IT IS RENDER-ONLY, AND THAT IS LOAD-BEARING. The authoritative server never evaluates ocean height —
 * ships are a 2D sim and buoyancy is cosmetic (grep: no `oceanHeightAt` under apps/server). So this
 * mutable state can never reach authority, prediction or the wire, and determinism (invariant 6) is
 * untouched. Defaults are the IDENTITY — wind along +x, chop gain 1 — so anything that never sets it
 * (tests, dragascene, the server if it ever samples) gets exactly the sea shipped in water-v2.
 */
export interface OceanEnv {
  /** cos/sin of the wind bearing the short chop band travels on */
  windCos: number; windSin: number;
  /** multiplier on the chop band's amplitude — the ambient sea state (glassy … breezy) */
  chopAmp: number;
}
export const OCEAN_ENV: OceanEnv = { windCos: 1, windSin: 0, chopAmp: 1 };

/** Point the short chop band down the LIVE wind and set the ambient chop gain. Render-only; see OCEAN_ENV. */
export function setOceanEnv(windDir: number, chopAmp: number): void {
  OCEAN_ENV.windCos = Math.cos(windDir);
  OCEAN_ENV.windSin = Math.sin(windDir);
  OCEAN_ENV.chopAmp = chopAmp;
}

/** This component's world direction right now: fixed for the swell, wind-rotated for the chop. */
const dirXOf = (w: WaveCoef): number => (w.chop ? w.dirX * OCEAN_ENV.windCos - w.dirY * OCEAN_ENV.windSin : w.dirX);
const dirYOf = (w: WaveCoef): number => (w.chop ? w.dirX * OCEAN_ENV.windSin + w.dirY * OCEAN_ENV.windCos : w.dirY);
/** Per-band gain: the swell is the swell, the chop breathes with the ambient sea state. */
const gainOf = (w: WaveCoef): number => (w.chop ? OCEAN_ENV.chopAmp : 1);

/**
 * Per-component amplitude at swell scale `amp`.
 *
 * TWO REGIMES, and the split is what keeps every existing caller honest:
 *   - `amp ≤ 1` (dead calm … the approved baseline): a PLAIN linear scale of the whole sea, so `amp = 0`
 *     still means a mirror-flat plane (dragascene, tests and any future "becalmed" mode rely on that) and
 *     `amp = 1` is the sea the owner signed off on, digit for digit.
 *   - `amp > 1` (storm): only the STORM EXCESS fans out by the per-component `swell` weight, so the long
 *     vaga rears up while the near-saturated chop barely grows.
 *
 * Branch-free (a min and a max), and it collapses to exactly `w.amp` at 1 — which is the whole reason a
 * calm screenshot is unchanged by all of this rather than merely close.
 */
const waveAmpAt = (w: WaveCoef, amp: number): number =>
  w.amp * (amp < 1 ? amp : 1) + w.amp * w.swell * (amp > 1 ? amp - 1 : 0);

/** Sum of all wave amplitudes at swell scale `amp` — the absolute max the sea can rise/fall. */
export function oceanMaxHeight(amp = 1): number {
  let s = 0;
  for (const w of OCEAN_WAVES) s += waveAmpAt(w, amp) * gainOf(w);
  return s;
}

/** Sum of all wave amplitudes at the calm baseline — the absolute max the sea can rise/fall. */
export const OCEAN_MAX_HEIGHT = oceanMaxHeight(1);

/**
 * Total Gerstner steepness Σ Q·k·A at swell scale `amp`.
 *
 * THE HARD PHYSICAL LIMIT: a Gerstner sum with Σ Q·k·A ≥ 1 folds through itself — the crest inverts and
 * the surface self-intersects (loops), which on screen is a shredded, flickering sea and in the CPU
 * buoyancy is a height field that is no longer a function of position. Every wave the table gains must
 * be paid for out of this budget. `ocean.test.ts` asserts it stays under a safety margin AT THE STORM
 * MAXIMUM (STORM.WAVE_SCALE), not merely in calm.
 */
export function oceanSteepnessSum(amp = 1, chopAmp = OCEAN_ENV.chopAmp): number {
  let s = 0;
  for (const w of OCEAN_WAVES) s += w.steep * w.k * waveAmpAt(w, amp) * (w.chop ? chopAmp : 1);
  return s;
}

/** Vertical water height at plane position (x, y) and sim time t (seconds). */
export function oceanHeightAt(x: number, y: number, t: number, amp = 1): number {
  let h = 0;
  for (let i = 0; i < OCEAN_WAVES.length; i++) {
    const w = OCEAN_WAVES[i]!;
    const phase = w.k * (dirXOf(w) * x + dirYOf(w) * y) - w.omega * t;
    h += waveAmpAt(w, amp) * gainOf(w) * Math.sin(phase);
  }
  return h;
}

/**
 * THE HULL'S ANSWER TO THE SEA — height filtered to the waves a ship of length `len` can actually feel.
 *
 * A hull is a 20 m rigid body. It does not rise 4 cm because a 1.4 m wavelet passed under the bow; it
 * spans fourteen of them and averages them to nothing. Sampling the FULL spectrum at four control points
 * and calling that buoyancy is what makes a boat jitter, and — at a storm crest, where the chop rides on
 * top of a 2.7 m vaga — what makes the water visibly climb over the deck. The owner's words for the
 * target: "a água nunca tampa o barco; ele desliza na ondulação."
 *
 * So the hull integrates the sea instead of point-sampling it. A wave of length L under a hull of length
 * `len` is attenuated by the mean of its own sine across the hull — the classic sinc window,
 * `sin(π·len/L) / (π·len/L)` — which is 1 for a swell far longer than the ship, crosses zero when exactly
 * one wavelength fits under it, and stays near zero for everything shorter. That is not a fudge factor;
 * it is what averaging a sine over a finite span actually gives, and it means the filter needs no
 * hand-tuned cutoff: the SHIP'S OWN LENGTH is the cutoff. A sloop feels a little more chop than a
 * galleon, for free and for the right reason.
 *
 * Render-only (nothing authoritative reads ocean height), and it deliberately does NOT change the drawn
 * surface: the sea still has every wavelet on it. This only changes what the hull *responds* to, which
 * is exactly the physical distinction between the water and the boat.
 */
export function oceanHullHeightAt(x: number, y: number, t: number, amp: number, len: number): number {
  let h = 0;
  for (let i = 0; i < OCEAN_WAVES.length; i++) {
    const w = OCEAN_WAVES[i]!;
    const u = Math.PI * len / w.length;
    const win = u < 1e-4 ? 1 : Math.sin(u) / u;              // sinc: the hull's own averaging window
    if (win < 0.02 && win > -0.02) continue;                   // fully averaged away — skip the sin entirely
    const phase = w.k * (dirXOf(w) * x + dirYOf(w) * y) - w.omega * t;
    h += waveAmpAt(w, amp) * gainOf(w) * win * Math.sin(phase);
  }
  return h;
}

/**
 * Full Gerstner sample: horizontal displacement + height. The client shader
 * uses all three; server buoyancy only needs `.height`, sampled at the
 * undisplaced control-point position (the horizontal pinch is cosmetic and
 * never affects authority).
 */
export interface OceanSample { dx: number; dy: number; height: number; }

/**
 * Alloc-free Gerstner sample: writes dx/dy/height into a caller-owned object and returns it.
 * Use this (with reused scratch) in per-frame hot paths so the render loop stays GC-free.
 */
export function oceanSampleInto(out: OceanSample, x: number, y: number, t: number, amp = 1): OceanSample {
  let dx = 0, dy = 0, h = 0;
  for (let i = 0; i < OCEAN_WAVES.length; i++) {
    const w = OCEAN_WAVES[i]!;
    const a = waveAmpAt(w, amp) * gainOf(w);
    const dX = dirXOf(w), dY = dirYOf(w);
    const phase = w.k * (dX * x + dY * y) - w.omega * t;
    const c = Math.cos(phase);
    h += a * Math.sin(phase);
    dx += w.steep * a * dX * c;
    dy += w.steep * a * dY * c;
  }
  out.dx = dx; out.dy = dy; out.height = h;
  return out;
}

/** Convenience wrapper that allocates a fresh result — fine for cold paths, NOT per-frame hot loops. */
export function oceanSampleAt(x: number, y: number, t: number, amp = 1): OceanSample {
  return oceanSampleInto({ dx: 0, dy: 0, height: 0 }, x, y, t, amp);
}

/**
 * The GLSL twin of `OCEAN_ENV` — two uniforms, declared once and shared by both generated shaders.
 *
 * `uChopDir` is (cos, sin) of the live wind, and `uChopAmp` the ambient chop gain. Only the SHORT band
 * reads them: the long swell is an ocean-scale system with days of momentum behind it and does not
 * swing round because the breeze did, while wind-chop by definition travels with the wind that raised
 * it. That split is why the sea can answer the wind without the horizon spinning.
 */
const OCEAN_ENV_GLSL = [
  'uniform vec2 uChopDir;   // (cos, sin) of the LIVE wind — the short chop band rides it',
  'uniform float uChopAmp;  // ambient sea state: the chop band\'s gain (glassy … breezy)',
].join('\n');

/**
 * The uniforms every consumer of the generated GLSL MUST declare, pre-set to the IDENTITY sea.
 *
 * Shipped from here, next to the generator that requires them, because the failure mode of forgetting
 * one is silent and ugly: an undeclared-but-referenced uniform reads as 0 in GL, so `uChopDir = (0,0)`
 * would collapse every chop wavenumber to zero and turn the short band into a whole-ocean plane pumping
 * up and down in unison. Spread this into the material's `uniforms` and the worst case is a sea that
 * simply never answers the wind.
 *
 * Plain data (no three.js in @piratas/sim) — three accepts a 2-element array wherever it wants a vec2.
 */
export const oceanEnvUniforms = (): { uChopDir: { value: number[] }; uChopAmp: { value: number } } =>
  ({ uChopDir: { value: [1, 0] }, uChopAmp: { value: 1 } });

/**
 * Emit the direction lines for one component: the authored literal, then — for the CHOP band only — the
 * rotation into the live wind frame.
 *
 * Rotating the DIRECTION rather than the sample point costs one extra op and is the only version that is
 * actually true: the Gerstner horizontal pinch has to point the way the wave TRAVELS, and rotating `p`
 * instead would leave the pinch aimed where the wave would have gone in a dead calm.
 *
 * Both lines are deliberately in the plainest possible form, because `ocean.test.ts` locks the CPU to the
 * shader by PARSING this emitted text rather than re-deriving the maths.
 */
function emitDir(w: WaveCoef): string[] {
  const lines = [`  d = vec2(${w.dirX.toFixed(6)}, ${w.dirY.toFixed(6)});`];
  if (w.chop) {
    lines.unshift('  bg = uChopAmp;');
    lines.push('  d = vec2(d.x * uChopDir.x - d.y * uChopDir.y, d.x * uChopDir.y + d.y * uChopDir.x);');
  }
  return lines;
}

/**
 * Generate GLSL that is mathematically identical to the CPU functions above.
 * Embed the returned string in the client's water vertex shader. Because it is
 * built from OCEAN_WAVES, the shader can never drift from server buoyancy.
 */
export function buildOceanGLSL(): string {
  const lines: string[] = [];
  lines.push('// AUTO-GENERATED from OCEAN_WAVES in @piratas/sim — do not hand-edit.');
  lines.push(OCEAN_ENV_GLSL);
  lines.push('vec3 oceanSample(vec2 p, float t, float amp) {');
  lines.push('  float h = 0.0; float dx = 0.0; float dy = 0.0; float phase; float c; float a; vec2 d;');
  lines.push('  float lo = min(amp, 1.0);        // linear below the calm baseline (amp 0 => a flat plane)');
  lines.push('  float sw = max(amp - 1.0, 0.0);  // the STORM EXCESS, fanned out per component below');
  lines.push('  float bg = 1.0;                  // band gain: 1 for the swell, uChopAmp for the chop');
  for (const w of OCEAN_WAVES) {
    const k = w.k.toFixed(6);
    const omega = w.omega.toFixed(6);
    const steep = w.steep.toFixed(6);
    lines.push(...emitDir(w));
    // per-component storm weight folded at generation time: a = A * (1 + (amp-1) * swell)
    lines.push(`  a = (${w.amp.toFixed(6)} * lo + ${(w.amp * w.swell).toFixed(6)} * sw) * bg;`);
    lines.push(`  phase = ${k} * (d.x * p.x + d.y * p.y) - ${omega} * t;`);
    lines.push(`  c = cos(phase);`);
    lines.push(`  h += a * sin(phase);`);
    lines.push(`  dx += ${steep} * a * d.x * c;`);
    lines.push(`  dy += ${steep} * a * d.y * c;`);
  }
  // returns (horizontalX, height, horizontalY) ready to add to a world XZ vertex.
  // `amp` is the STORM SWELL SCALE (1 = calm baseline). It is NOT a flat multiplier: each component
  // takes the share of it declared by its `swell` weight, so the long vaga rears up while the short
  // chop stays near saturation. Kept in lockstep with the CPU oceanHeightAt/oceanSampleAt amp param
  // (same fan-out, same constants) so bob/wake can never drift from the rendered surface.
  lines.push('  return vec3(dx, h, dy);');
  lines.push('}');
  lines.push('float oceanHeight(vec2 p, float t, float amp) { return oceanSample(p, t, amp).y; }');
  return lines.join('\n');
}

/**
 * Emit `float oceanSwellHeight(vec2 p, float t, float amp)` — the height of the LONG band ONLY.
 *
 * WHY IT EXISTS (the "quilt"/colcha fix). Three shading terms take a THRESHOLD of the sea height: the
 * deep→shallow depth gradient, the broad crest sheen, and the storm's whitecap mask. Fed the FULL nine
 * components, a threshold does not sample "how high is this water" — it samples "where do all nine sines
 * happen to peak together", and nine sines crossing at nine fixed angles have a quasi-periodic
 * INTERFERENCE LATTICE. Photographed from a tactical zoom-out, that lattice prints as a regular quilt of
 * pale cells over the whole ocean, brightest exactly where nothing physical is happening.
 *
 * The near field already dodges it: `buildOceanNormalGLSL` hands back a swell-only (pinch, height) pair
 * and the lit path thresholds THAT. But the per-pixel path is distance-gated (past ~280 m it is skipped
 * for good reason), and the low-quality path never had it at all — so both fell back to the full height
 * and the lattice printed everywhere the analytic normal had faded out. This gives the VERTEX stage the
 * same swell-only number for four extra sines on ~22 k vertices (measured: free), so every thresholded
 * read in either path can follow the WAVE while the chop keeps doing its job, which is to texture the
 * surface through the normal.
 *
 * Generated from OCEAN_WAVES for the same reason every other emitter here is: a hand-written copy would
 * drift the instant WAVE_DEFS is retuned. Identical per-component amplitude fan-out as `oceanSample`,
 * so `oceanSwellHeight` is exactly the `h` of `oceanSample` with the chop terms omitted.
 */
export function buildOceanSwellGLSL(): string {
  const lines: string[] = [];
  lines.push('// AUTO-GENERATED from OCEAN_WAVES in @piratas/sim — do not hand-edit.');
  lines.push('float oceanSwellHeight(vec2 p, float t, float amp) {');
  lines.push('  float h = 0.0; float phase; float a; vec2 d;');
  lines.push('  float lo = min(amp, 1.0);');
  lines.push('  float sw = max(amp - 1.0, 0.0);');
  for (const w of OCEAN_WAVES) {
    if (w.chop) continue; // the LONG band only — that is the whole point
    lines.push(`  d = vec2(${w.dirX.toFixed(6)}, ${w.dirY.toFixed(6)});`);
    lines.push(`  a = ${w.amp.toFixed(6)} * lo + ${(w.amp * w.swell).toFixed(6)} * sw;`);
    lines.push(`  phase = ${w.k.toFixed(6)} * (d.x * p.x + d.y * p.y) - ${w.omega.toFixed(6)} * t;`);
    lines.push('  h += a * sin(phase);');
  }
  lines.push('  return h;');
  lines.push('}');
  return lines.join('\n');
}

/** Sum of the LONG-band amplitudes at swell scale `amp` — the range `oceanSwellHeight` spans. */
export function oceanSwellMaxHeight(amp = 1): number {
  let s = 0;
  for (const w of OCEAN_WAVES) if (!w.chop) s += waveAmpAt(w, amp);
  return s;
}

/**
 * Generate GLSL for the ANALYTIC surface normal (and, for free, the exact height) of the very same Gerstner
 * sum the vertex shader displaced with.
 *
 * Render-only (lighting), and deliberately GENERATED from OCEAN_WAVES for the same reason the height GLSL is:
 * a hand-written copy of the wave math inside the client shader would silently drift the moment WAVE_DEFS
 * changes. It displaces NOTHING — the geometry stays exactly what `oceanSample` produced; this only says which
 * way that geometry is FACING, and how high it is, per PIXEL instead of per (coarse) triangle.
 *
 * `p` is the UNDISPLACED plane position (the same p you fed `oceanSample`), `amp` the same swell scale.
 * Normal — GPU Gems 1, ch. 1, the first-order Jacobian of the Gerstner map:
 *   N = normalize( -Σ Aᵢ k D.x cos φ , 1 - Σ Q Aᵢ k sin φ , -Σ Aᵢ k D.y cos φ )
 * Height — bit-for-bit the same closed form as `oceanHeightAt` / `oceanSample().y`: Σ Aᵢ sin φ (with the
 * same per-component storm weight Aᵢ = A·(1 + (amp−1)·swell) the displacement used). Both reuse the sin/cos
 * the normal already needed, so the height rides along for one multiply-add per wave.
 * Returns vec4(normal.xyz world-space +Y up, height in metres).
 *
 * `chop` (0..1) is the DISTANCE GATE FOR THE SHORT BAND, and it is the one place the enriched spectrum is
 * allowed to cost less than the full table:
 *
 *   - The VERTEX shader always evaluates every component (see `buildOceanGLSL`) — the geometry is what the
 *     CPU bob, the wake and the LOD-seam stitch all agree with, so it may never be thinned. Measured, that
 *     is ~22 k vertices × 5 extra waves ≈ 0.004 ms: free.
 *   - The FRAGMENT shader re-evaluates the table PER PIXEL inside the analytic-normal gate. There the five
 *     extra sin/cos are ~0.17 ms of ALU at 1600×900, so the chop band lives behind its own `chop > 0.004`
 *     branch, which is screen-coherent (distance varies smoothly down the screen) and therefore genuinely
 *     skipped on far pixels. A 1.55 m wavelet is sub-pixel past ~60 m anyway — fading it out is not a
 *     compromise, it is the correct anti-aliasing.
 *
 * Pass `chop = 1.0` for the full table (that is the configuration ocean.test.ts locks to the CPU).
 */
export function buildOceanNormalGLSL(): string {
  const lines: string[] = [];
  const emit = (w: WaveCoef): void => {
    const k = w.k.toFixed(6);
    const omega = w.omega.toFixed(6);
    // fold the constants at generation time: A*k for the slope, Q*A*k for the crest pinch. The storm
    // fan-out A -> A*(1 + (amp-1)*swell) is folded the same way, so each line stays two constants. The
    // per-component DIRECTION is the one thing that cannot be folded any more (the chop rotates with the
    // live wind), so it multiplies in from `d` — 2 extra ALU per component.
    const ak = w.amp * w.k;
    const akS = ak * w.swell;
    lines.push(...emitDir(w));
    lines.push(`  phase = ${k} * (d.x * p.x + d.y * p.y) - ${omega} * t;`);
    lines.push(`  c = cos(phase) * g * bg; s = sin(phase) * g * bg;`);
    lines.push(`  nx += (${ak.toFixed(6)} * lo + ${akS.toFixed(6)} * sw) * d.x * c;`);
    lines.push(`  nz += (${ak.toFixed(6)} * lo + ${akS.toFixed(6)} * sw) * d.y * c;`);
    lines.push(`  ny += (${(w.steep * ak).toFixed(6)} * lo + ${(w.steep * akS).toFixed(6)} * sw) * s;`);
    lines.push(`  h  += (${w.amp.toFixed(6)} * lo + ${(w.amp * w.swell).toFixed(6)} * sw) * s;`);
  };
  lines.push('// AUTO-GENERATED from OCEAN_WAVES in @piratas/sim — do not hand-edit.');
  lines.push(OCEAN_ENV_GLSL);   // this generator lands in the FRAGMENT stage; the displacement one in the vertex
  lines.push('vec4 oceanNormalHeight(vec2 p, float t, float amp, float chop, out vec2 swellNH) {');
  lines.push('  float nx = 0.0; float nz = 0.0; float ny = 0.0; float h = 0.0; float phase; float c; float s; vec2 d;');
  lines.push('  float lo = min(amp, 1.0);        // linear below the calm baseline (amp 0 => a flat plane)');
  lines.push('  float sw = max(amp - 1.0, 0.0);  // the STORM EXCESS, fanned out per component below');
  lines.push('  float bg = 1.0;         // band gain: 1 for the swell, uChopAmp for the chop');
  lines.push('  float g = 1.0;          // distance gate: 1 for the swell, `chop` for the short band');
  for (const w of OCEAN_WAVES) if (!w.chop) emit(w);
  lines.push('  swellNH = vec2(ny, h);  // the SWELL-ONLY pinch + height, free: consumers that must not');
  lines.push('                          // see the chop (storm foam masks) would otherwise pay a 2nd call');
  lines.push('  if (chop > 0.004) {     // screen-coherent: far pixels genuinely skip the short band');
  lines.push('    g = chop;');
  for (const w of OCEAN_WAVES) if (w.chop) emit(w);
  lines.push('  }');
  lines.push('  return vec4(normalize(vec3(-nx, 1.0 - ny, -nz)), h);');
  lines.push('}');
  return lines.join('\n');
}

/* ------------------------------------------------------------------------------------------------ *
 *  COAST ENVELOPE — the storm swell must not roll THROUGH a beach.
 * ------------------------------------------------------------------------------------------------ */

/** Coast zones the shader loop / CPU helper handle at once (the N nearest islands). */
export const OCEAN_COAST_MAX = 8;

/**
 * Storm-swell damping near land, 0..1 (1 = open sea, 0 = fully damped in the shoal).
 *
 * A 2 m open-ocean vaga arriving at a sand spit would render as a wall of water standing ON the beach —
 * the sea mesh has no idea the island is there. Real swell SHOALS: it feels the bottom, slows, and its
 * energy is spent well before the sand. This is that envelope, and it is shared on purpose: the client
 * vertex shader, the per-pixel normal and the CPU hull bob all read the SAME function (the GLSL below is
 * generated from this very code path), so a ship near a beach bobs on exactly the water it can see.
 *
 * NOTE it damps only the storm EXCESS: callers use it as `amp = mix(1, waveScale, coast)`, so the
 * approved calm sea still laps the shore exactly as it does today.
 *
 * `zones` is a flat [x, y, rInner, rOuter, …] array (stride 4); `n` is how many are live. The NEAREST
 * zone wins (min, not a product — two islands 200 m apart must not multiply into a dead flat pond).
 */
export function oceanCoastFactor(x: number, y: number, zones: ArrayLike<number>, n: number): number {
  let f = 1;
  for (let i = 0; i < n; i++) {
    const o = i * 4;
    const dx = x - (zones[o] ?? 0), dy = y - (zones[o + 1] ?? 0);
    const ri = zones[o + 2] ?? 0, ro = zones[o + 3] ?? 1;
    const d = Math.sqrt(dx * dx + dy * dy);
    let u = (d - ri) / Math.max(1e-3, ro - ri);
    u = u < 0 ? 0 : u > 1 ? 1 : u;
    const s = u * u * (3 - 2 * u);
    if (s < f) f = s;
  }
  return f;
}

/**
 * Generate the GLSL twin of `oceanCoastFactor`. Same smoothstep, same min-reduction, same clamp — written
 * out here rather than by hand in render.ts for exactly the reason the wave GLSL is generated: a hand copy
 * drifts the moment the envelope is retuned, and a drifting envelope means the hull bobs on one sea while
 * the eye sees another.
 *
 * Declares `uniform vec4 uCoast[N]` (xy = centre, z = inner radius, w = outer radius) and `uniform int
 * uCoastCnt`. With `uCoastCnt == 0` the loop breaks immediately — calm water pays one integer compare.
 */
export function buildOceanCoastGLSL(max = OCEAN_COAST_MAX): string {
  return [
    '// AUTO-GENERATED from oceanCoastFactor in @piratas/sim — do not hand-edit.',
    `uniform vec4 uCoast[${max}];`,
    'uniform int uCoastCnt;',
    'float oceanCoast(vec2 p) {',
    '  float f = 1.0;',
    `  for (int i = 0; i < ${max}; i++) {`,
    '    if (i >= uCoastCnt) break;',
    '    vec4 z = uCoast[i];',
    '    float u = clamp((distance(p, z.xy) - z.z) / max(1e-3, z.w - z.z), 0.0, 1.0);',
    '    f = min(f, u * u * (3.0 - 2.0 * u));',
    '  }',
    '  return f;',
    '}',
  ].join('\n');
}
